import { useRef } from "react";
import { MapPin, Phone, Mail, Instagram, Train, Car, Bus } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const HOURS = [
  ["Montag", "12:00 – 22:00 Uhr"],
  ["Dienstag", "Ruhetag", true],
  ["Mittwoch", "12:00 – 22:00 Uhr"],
  ["Donnerstag", "12:00 – 22:00 Uhr"],
  ["Freitag", "12:00 – 22:00 Uhr"],
  ["Samstag", "12:00 – 22:00 Uhr"],
  ["Sonntag", "12:00 – 22:00 Uhr"],
];

const day = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][
  new Date().getDay()
];

export default function Hours() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section
      id="kontakt"
      ref={ref}
      data-testid="hours-section"
      className="relative bg-[var(--niko-cream-2)] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Hours */}
          <div>
            <p className="reveal divider-leaf max-w-[220px] mb-8">
              Öffnungszeiten
            </p>
            <h2
              className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--niko-charcoal)] leading-[1.05]"
              data-testid="hours-headline"
            >
              Wann Sie uns <span className="italic text-[var(--niko-blue)]">finden</span>
            </h2>

            <ul className="reveal reveal-delay-2 mt-10 divide-y divide-[var(--niko-charcoal)]/10 border-y border-[var(--niko-charcoal)]/10">
              {HOURS.map(([d, h, closed], i) => {
                const isToday = d === day;
                return (
                  <li
                    key={d}
                    data-testid={`hours-row-${i}`}
                    className={`flex items-center justify-between py-4 px-3 transition-colors ${
                      isToday ? "bg-[var(--niko-gold)]/12" : ""
                    }`}
                  >
                    <span
                      className={`text-sm tracking-wide ${
                        isToday
                          ? "text-[var(--niko-charcoal)] font-semibold"
                          : "text-[var(--niko-charcoal)]/85"
                      }`}
                    >
                      {d}
                      {isToday && (
                        <span className="ml-3 text-[10px] tracking-[0.2em] uppercase text-[var(--niko-blue)] bg-white px-2 py-0.5 rounded-full">
                          Heute
                        </span>
                      )}
                    </span>
                    <span
                      className={`font-greek text-lg ${
                        closed ? "text-[var(--niko-charcoal)]/40 italic" : "text-[var(--niko-charcoal)]"
                      }`}
                    >
                      {h} {closed && "❌"}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="reveal mt-5 text-xs text-[var(--niko-charcoal)]/55">
              Küche bis ca. 21:30 Uhr · Letzte Bestellungen werden zuvor angenommen.
            </p>
          </div>

          {/* Contact */}
          <div>
            <p className="reveal divider-leaf max-w-[200px] mb-8">Kontakt</p>
            <h2
              className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--niko-charcoal)] leading-[1.05]"
              data-testid="contact-headline"
            >
              Anfahrt &amp; <span className="italic text-[var(--niko-blue)]">Adresse</span>
            </h2>

            <ul className="reveal reveal-delay-2 mt-10 space-y-5">
              <ContactRow icon={MapPin} primary="Höntroper Str. 2" secondary="44869 Bochum" />
              <ContactRow
                icon={Phone}
                primary="+49 2327 995454"
                href="tel:+492327995454"
                testid="contact-phone"
              />
              <ContactRow
                icon={Mail}
                primary="NikosGrillBochum@gmail.com"
                href="mailto:NikosGrillBochum@gmail.com"
                testid="contact-email"
              />
              <ContactRow
                icon={Instagram}
                primary="@nikosgrillhoentrop"
                href="https://www.instagram.com/nikosgrillhoentrop"
                testid="contact-instagram"
              />
            </ul>

            <div className="reveal reveal-delay-3 mt-10 pt-8 border-t border-[var(--niko-charcoal)]/10">
              <p className="text-xs tracking-[0.22em] uppercase text-[var(--niko-charcoal)]/55 mb-5">
                So erreichen Sie uns
              </p>
              <div className="space-y-4 text-sm text-[var(--niko-charcoal)]/80">
                <div className="flex items-center gap-3">
                  <Train className="w-4 h-4 text-[var(--niko-blue)]" />
                  <span>S-Bahn: Bochum-Wattenscheid-Höntrop (380 m)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Car className="w-4 h-4 text-[var(--niko-blue)]" />
                  <span>Parken: Kostenlos am Straßenrand</span>
                </div>
                <div className="flex items-center gap-3">
                  <Bus className="w-4 h-4 text-[var(--niko-blue)]" />
                  <span>Bus-Haltestelle direkt in der Nähe</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div
          className="reveal mt-20 rounded-sm overflow-hidden border border-[var(--niko-charcoal)]/10"
          data-testid="map-embed"
        >
          <iframe
            title="Niko's Grill – Standort"
            src="https://www.google.com/maps?q=H%C3%B6ntroper+Str.+2,+44869+Bochum&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, primary, secondary, href, testid }) {
  const Inner = (
    <>
      <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--niko-blue)]/8 flex items-center justify-center text-[var(--niko-blue)] group-hover:bg-[var(--niko-gold)] group-hover:text-[var(--niko-dark)] transition-colors">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-sm font-medium text-[var(--niko-charcoal)]">{primary}</p>
        {secondary && (
          <p className="text-xs text-[var(--niko-charcoal)]/55">{secondary}</p>
        )}
      </div>
    </>
  );
  return (
    <li>
      {href ? (
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel="noopener noreferrer"
          data-testid={testid}
          className="group flex items-center gap-4"
        >
          {Inner}
        </a>
      ) : (
        <div className="group flex items-center gap-4">{Inner}</div>
      )}
    </li>
  );
}
